import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import dayjs from 'dayjs';
import { HiltiDailyWorkRecord, HiltiProduct, HiltiWorker } from '../types';
import { hiltiDailyWorkAPI, hiltiProductsAPI, hiltiWorkersAPI } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const HiltiProduction: React.FC = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const [records, setRecords] = useState<HiltiDailyWorkRecord[]>([]);
  const [workers, setWorkers] = useState<HiltiWorker[]>([]);
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiDailyWorkRecord | null>(null);
  const [formProducts, setFormProducts] = useState<Array<{ productId: string; quantity: number }>>([]);
  const [activeWorker, setActiveWorker] = useState<HiltiWorker | null>(null);

  const loadBaseData = async () => {
    setLoading(true);
    setError('');
    try {
      const [prodList, workersList] = await Promise.all([
        hiltiProductsAPI.getAll(),
        hiltiWorkersAPI.getAll({ showInactive: false })
      ]);
      setProducts(prodList);
      setWorkers(workersList);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const params: { date?: string } = {};
      if (date) {
        params.date = dayjs(date).format('YYYY-MM-DD');
      }
      const data = await hiltiDailyWorkAPI.getAll(params);
      
      // Normalizuj workerId na string (_id) zbog populate na backendu - kao u HiltiReports
      const normalized = data.map((rec: any) => {
        const workerId: any = rec.workerId;
        let normalizedWorkerId: string;
        
        if (typeof workerId === 'string') {
          normalizedWorkerId = workerId;
        } else if (workerId && typeof workerId === 'object' && '_id' in workerId) {
          normalizedWorkerId = String(workerId._id);
        } else {
          return null;
        }

        return {
          ...rec,
          workerId: normalizedWorkerId
        };
      }).filter((rec): rec is HiltiDailyWorkRecord => rec !== null);
      
      setRecords(normalized);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvodnje';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBaseData();
  }, []);

  useEffect(() => {
    void loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const productOptions = useMemo(
    () => products.map((p) => ({ label: p.name, value: p._id })),
    [products]
  );

  const normalizeName = (value: string) =>
    value
      .normalize('NFD') // razdvoji slova i dijakritike (ć -> c + ˇ)
      .replace(/[\u0300-\u036f]/g, '') // ukloni dijakritike
      .replace(/\s+/g, ' ') // višestruke razmake na jedan
      .trim()
      .toLowerCase();

  const normalizeNameTokens = (value: string) =>
    normalizeName(value)
      .split(' ')
      .filter(Boolean)
      .sort()
      .join(' ');

  // Kreiraj memoizovanu mapu zapisa po workerId - jednostavnije kao u HiltiReports
  const recordsByWorkerId = useMemo(() => {
    const map = new Map<string, HiltiDailyWorkRecord>();
    records.forEach((rec) => {
      // Osiguraj da workerId je uvek string za konzistentno mapiranje
      const workerIdKey = String(rec.workerId);
      map.set(workerIdKey, rec);
    });
    return map;
  }, [records]);

  const onOpenForWorker = (worker: HiltiWorker) => {
    setActiveWorker(worker);
    const workerIdKey = String(worker._id);
    const existing = recordsByWorkerId.get(workerIdKey);
    setEditing(existing || null);
    setFormProducts(
      existing?.products?.map((p) => ({
        productId: typeof p.productId === 'string' ? p.productId : p.productId._id,
        quantity: p.quantity
      })) || [{ productId: '', quantity: 0 }]
    );
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!activeWorker) {
      setError('Radnik je obavezan.');
      return;
    }
    if (!date) {
      setError('Datum je obavezan.');
      return;
    }
    const cleaned = formProducts
      .filter((p) => p.productId && p.quantity > 0)
      .map((p) => ({ productId: p.productId, quantity: p.quantity }));
    if (cleaned.length === 0) {
      setError('Dodajte bar jedan proizvod.');
      return;
    }

    const payload = {
      workerId: activeWorker._id,
      date: dayjs(date).format('YYYY-MM-DD'),
      products: cleaned
    };

    try {
      setError('');
      if (editing) {
        await hiltiDailyWorkAPI.update(editing._id, payload);
        showCustomAlert.success('Proizvodnja je ažurirana');
      } else {
        await hiltiDailyWorkAPI.create(payload);
        showCustomAlert.success('Proizvodnja je dodata');
      }
      setDialogVisible(false);
      await loadRecords();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju proizvodnje';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = async (rec: HiltiDailyWorkRecord) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj zapis proizvodnje?')) return;
    try {
      await hiltiDailyWorkAPI.delete(rec._id);
      await loadRecords();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju zapisa';
      showCustomAlert.error(msg);
    }
  };
  // Koristimo recordsByWorkerId mapu za brže pristupanje
  const statusBody = (worker: HiltiWorker) => {
    const workerIdKey = String(worker._id);
    const rec = recordsByWorkerId.get(workerIdKey);
    if (!rec || !rec.products || rec.products.length === 0) {
      return <Tag value="Nije uneo" severity="warning" />;
    }
    return <Tag value="Uneto" severity="success" />;
  };

  const productsBody = (worker: HiltiWorker) => {
    const workerIdKey = String(worker._id);
    const rec = recordsByWorkerId.get(workerIdKey);
    if (!rec || !rec.products || rec.products.length === 0) return '-';
    return rec.products
      .map((p) => {
        // productId može biti string ili objekat
        const name = typeof p.productId === 'object' && p.productId?.name 
          ? p.productId.name 
          : (p.productName || 'Nepoznat proizvod');
        return `${name} (${p.quantity})`;
      })
      .join(', ');
  };

  const actionsBodyWorkers = (worker: HiltiWorker) => {
    const workerIdKey = String(worker._id);
    const rec = recordsByWorkerId.get(workerIdKey);
    const hasRecord = rec && rec.products && rec.products.length > 0;
    
    return (
      <div className="flex justify-content-center gap-1">
        {hasRecord ? (
          <>
            <Button
              icon="pi pi-pencil"
              rounded
              severity="danger"
              onClick={() => onOpenForWorker(worker)}
              tooltip="Izmeni proizvodnju"
            />
            <Button
              icon="pi pi-trash"
              rounded
              severity="danger"
              onClick={() => handleDelete(rec!)}
              tooltip="Obriši proizvodnju"
            />
          </>
        ) : (
          <Button
            icon="pi pi-plus"
            rounded
            onClick={() => onOpenForWorker(worker)}
            tooltip="Dodaj proizvodnju"
          />
        )}
      </div>
    );
  };


  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Hilti Proizvodnja">
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="flex align-items-center gap-2">
            <span style={{ fontWeight: 600 }}>Datum:</span>
            <Calendar
              value={date}
              onChange={(e) => setDate(e.value as Date | null)}
              dateFormat="dd.mm.yy"
              showIcon
              appendTo="self"
            />
          </div>
          <Button
            label="Osveži"
            icon="pi pi-refresh"
            severity="secondary"
            outlined
            onClick={loadRecords}
          />
        </div>

        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={workers}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage="Nema radnica"
          responsiveLayout="stack" breakpoint="768px"
          className="hilti-production-table"
          key={`table-${records.length}-${date?.getTime()}`}
        >
          <Column
            field="lastName"
            header="Radnik"
            body={(w: HiltiWorker) => `${w.firstName} ${w.lastName}`}
            sortable
          />
          <Column header="Status" body={statusBody} style={{ width: '160px' }} />
          <Column header="Proizvodi" body={productsBody} />
          <Column header="Akcije" body={actionsBodyWorkers} style={{ width: '100px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={
          activeWorker && date
            ? `${activeWorker.firstName} ${activeWorker.lastName} - ${dayjs(date).format(
                'DD. MM. YYYY.'
              )}`
            : 'Unos proizvodnje'
        }
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid">
          <div className="field mb-3">
            <small className="text-600">
              Dodajte proizvode koje je radnik napravio za izabrani datum.
            </small>
          </div>

          {formProducts.map((row, idx) => (
            <div key={idx} className="grid formgrid align-items-center mb-2">
              <div className="field col-12 md:col-6">
                <Dropdown
                  value={row.productId}
                  options={productOptions}
                  onChange={(e) =>
                    setFormProducts((prev) =>
                      prev.map((r, i) =>
                        i === idx ? { ...r, productId: e.value as string } : r
                      )
                    )
                  }
                  placeholder="Izaberite proizvod"
                  className="w-full"
                  appendTo="self"
                />
              </div>
              <div className="field col-8 md:col-4">
                <InputNumber
                  value={row.quantity}
                  onValueChange={(e) =>
                    setFormProducts((prev) =>
                      prev.map((r, i) =>
                        i === idx ? { ...r, quantity: e.value || 0 } : r
                      )
                    )
                  }
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  className="w-full"
                />
              </div>
              <div className="field col-4 md:col-2 flex justify-content-end">
                <Button
                  icon="pi pi-trash"
                  rounded
                  text
                  severity="danger"
                  onClick={() =>
                    setFormProducts((prev) => prev.filter((_, i) => i !== idx))
                  }
                />
              </div>
            </div>
          ))}

          <Button
            label="Dodaj proizvod"
            icon="pi pi-plus"
            text
            onClick={() =>
              setFormProducts((prev) => [...prev, { productId: '', quantity: 0 }])
            }
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSave} />
          <Button
            label="Otkaži"
            icon="pi pi-times"
            severity="secondary"
            outlined
            onClick={() => setDialogVisible(false)}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default HiltiProduction;


